import { createContext, useState, useEffect, ReactNode } from 'react';

const getRootDom = function () {
  return document.getElementById('root');
};

const getRootFontSize = function () {
  return parseFloat(window.getComputedStyle(document.body).getPropertyValue('font-size'));
};

const debounce = function <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: any;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
};

interface DeviceContextProps {
  isMobile: boolean;
  isMobileView: boolean;
  isDesktop: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
  viewWidth: number;
  viewHeight: number;
  rootFontSize: number;
}

const startData: DeviceContextProps = {
  isMobile: false,
  isMobileView: false,
  isDesktop: true,
  isAndroid: false,
  isIOS: false,
  isPortrait: true,
  isLandscape: false,
  viewWidth: 0,
  viewHeight: 0,
  rootFontSize: 16,
};

export const DeviceContext = createContext<DeviceContextProps>(startData);

interface DeviceProviderProps {
  children: ReactNode;
}

export const DeviceProvider = ({ children }: DeviceProviderProps) => {
  const [deviceData, setDeviceData] = useState<DeviceContextProps>({
    ...startData,
  });

  useEffect(() => {
    let root: any = null;
    let html: any = null;
    let isActive = true;
    let isMobile = false;
    let isAndroid = false;
    let isIOS = false;
    let isOn = false;

    const setData = () => {
      if (isActive) {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const isLandscape =
          width >= 480 &&
          (isMobile || width <= 768) &&
          window.matchMedia('(orientation: landscape)').matches;
        const rootFontSize = getRootFontSize();

        const data: DeviceContextProps = {
          isMobile: isMobile,
          isMobileView: isMobile || width <= 768,
          isDesktop: !isMobile,
          isAndroid: isAndroid,
          isIOS: isIOS,
          isPortrait: !isLandscape,
          isLandscape: isLandscape,
          viewWidth: width,
          viewHeight: height,
          rootFontSize: rootFontSize,
        };

        if (root) {
          root.dataset.isMobile = data.isMobile;
          root.dataset.isMobileView = data.isMobileView;
          root.dataset.isDesktop = data.isDesktop;
          root.dataset.isAndroid = data.isAndroid;
          root.dataset.isIos = data.isIOS;
          root.dataset.isPortrait = data.isPortrait;
          root.dataset.isLandscape = data.isLandscape;
        }
        if (html) {
          // html.style.fontSize = data.isMobileView ? (data.viewWidth > 375 ? 16 : (16 * data.viewWidth / 375)) + 'px' : '';
          html.style.setProperty('--viewport-width', data.viewWidth);
          html.style.setProperty('--viewport-height', data.viewHeight);
        }

        setDeviceData(data);
      }
    };

    const setDataOunce = debounce(setData, 100);

    const userAgent =
      (navigator as any).userAgent || (navigator as any).vendor || (window as any).opera;
    (function (a) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        isMobile = true;
      isAndroid = !!a.match(/Android/i);
      isIOS = !!a.match(/iPhone|iPad|iPod/i);
    })(userAgent);
    root = getRootDom();
    html = document.querySelector('html');
    setData();
    isOn = true;
    window.addEventListener('resize', setDataOunce);

    return () => {
      isActive = false;
      if (isOn) {
        isOn = false;
        window.removeEventListener('resize', setDataOunce);
      }
    };
  }, []);

  return <DeviceContext.Provider value={deviceData}>{children}</DeviceContext.Provider>;
};
